// deps
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal deps
import { CartContext } from "@cart-client/contexts/CartContext";
import { getCartId, getRestaurantId } from "@cart-client/helpers/Cart";
import { CouponModalIds, GlobalOverlayPriority, OverlayType } from "@swiggy-private/cart-types";
import { AnalyticsContext, CartOverlaysContext } from "@swiggy-private/checkout-core";
import { LOCAL_STORAGE_KEYS } from "@swiggy-private/core-ui";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// analytics
import { clickDismissToingPopup, clickYayToingPopup, impressionToingPopup } from "@cart-client/analytics/events";

// constants
import { LABELS } from "@swiggy-private/cart-ui";

const useToingPopup = () => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { registerOverlays } = useContext(CartOverlaysContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const [showToingPopup, setShowToingPopup] = useState(false);

    const showLowestPriceGuaranteedPopup = useMemo(
        () => cartResponse?.data?.toing_presentational_config?.show_lowest_price_guaranteed_popup ?? false,
        [cartResponse?.data?.toing_presentational_config?.show_lowest_price_guaranteed_popup],
    );

    const cartId = getCartId(cartResponse);

    const restaurantId = getRestaurantId(cartResponse);
    const sessionId = cartResponse?.sid;

    const key = useMemo(() => {
        if (isEmpty(restaurantId) || isEmpty(sessionId)) return null;
        return `${restaurantId}-${sessionId}`;
    }, [restaurantId, sessionId]);

    useEffect(() => {
        if (isEmpty(key) || !showLowestPriceGuaranteedPopup) {
            setShowToingPopup(false);
            return;
        }

        const toingFrequencyCap = localStorage.getItem(LOCAL_STORAGE_KEYS.TOING_POPUP_FREQUENCY_CAPPING_KEY);

        if (!isEmpty(toingFrequencyCap) && key === toingFrequencyCap) {
            setShowToingPopup(false);
        } else {
            setShowToingPopup(true);
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.TOING_AUTO_APPLIED_COUPON,
                priority: GlobalOverlayPriority.TOING_AUTO_APPLIED_COUPON,
            });
            localStorage.setItem(LOCAL_STORAGE_KEYS.TOING_POPUP_FREQUENCY_CAPPING_KEY, key);
        }
    }, [key, registerOverlays, showLowestPriceGuaranteedPopup]);

    const onClickDismissToingPopup = useCallback(() => {
        clickDismissToingPopup(clickEvent, JSON.stringify({ cartId }));
    }, [cartId, clickEvent]);

    const onClickYayToingPopup = useCallback(() => {
        clickYayToingPopup(clickEvent, JSON.stringify({ cartId }));
    }, [cartId, clickEvent]);

    const handleToingPopupClose = useCallback(
        (label?: string) => {
            setShowToingPopup(false);
            if (label === LABELS.CLOSE_CLICK) {
                onClickDismissToingPopup();
            } else if (label === LABELS.BUTTON_CLICK) {
                onClickYayToingPopup();
            }
        },
        [onClickDismissToingPopup, onClickYayToingPopup],
    );

    const onImpressionToingPopup = useCallback(() => {
        impressionToingPopup(impressionEvent, JSON.stringify({ cartId }));
    }, [cartId, impressionEvent]);

    return {
        showToingPopup: showLowestPriceGuaranteedPopup && showToingPopup,
        handleToingPopupClose,
        onImpressionToingPopup,
    };
};

export default useToingPopup;
